/**
 * LE TRACÉ D'UN ÉLÈVE, VU DE SON TUTEUR — atelier de figures, lot L7 (AB#943 ;
 * `docs/pilotage/atelier-figures/CONCEPTION.md` § 4). Ce que l'écran en
 * DÉCIDE vit ici, pour être éprouvé sans navigateur :
 *
 *  - LA CONSTRUCTION REJOUÉE : les étapes de l'élève (`constructionDeLEleve`,
 *    partagé — les mêmes que le serveur a relues), l'étape où elle DÉVIE (la
 *    dernière qui a touché l'objet de la première contrainte manquée), et la
 *    relecture « On a lu… » que l'élève a confirmée avant de soumettre
 *    (`decrireGesteDeTrace`, la même phrase que son écran) ;
 *  - LA NOTE DU TUTEUR : les objets que le tuteur peut toucher, et l'objet
 *    désigné par un clic sur la figure (l'aimant de l'atelier).
 *
 * Lecture seule et pur : aucun modèle, aucune écriture (I-11). Le verdict
 * n'est ni recalculé ni discuté : il est celui de la copie.
 */
import {
  aimanter,
  constructionDeLEleve,
  decrireGesteDeTrace,
  estIdEnonce,
  etapeDeDeviation,
  objetsDesignables,
  resoudreConstruction,
  type ConstructionDeLEleve,
  type FigureV2Plan,
  type IdObjet,
  type ObjetDesignable,
  type PointV2,
  type TraceDeTravailDto,
} from '@silex/shared';

import { planDEnonce } from './figures-v2-web';

/** Ce que la console montre de la construction d'un élève. */
export interface RejeuDuTrace {
  readonly construction: ConstructionDeLEleve;
  /** Le rang de l'étape où la construction dévie ; `null` : rien à montrer. */
  readonly deviation: number | null;
  /** La phrase du constat qui a désigné l'objet fautif ; `null` : aucun. */
  readonly constatFautif: string | null;
  /** « On a lu : … » — ce que l'élève a relu et confirmé avant de soumettre. */
  readonly gesteLu: string;
  readonly enonce: FigureV2Plan | null;
}

/**
 * La construction rejouée d'un tracé gardé. La déviation est cherchée par la
 * PREMIÈRE contrainte manquée qui retient un objet de l'élève : c'est la même
 * que la correction dit à l'élève en premier.
 */
export function rejeuDuTrace(trace: TraceDeTravailDto, figureDEnonce: unknown): RejeuDuTrace {
  const enonce = planDEnonce(figureDEnonce);
  const construction = constructionDeLEleve(trace.eleve, trace.historique ?? null, enonce);
  const fautif = trace.constats.find((c) => c.etat !== 'tenue' && typeof c.objet === 'string');
  return {
    construction,
    deviation: etapeDeDeviation(construction, trace.eleve, fautif?.objet ?? null),
    constatFautif: fautif?.texte ?? null,
    gesteLu: decrireGesteDeTrace(trace.eleve, enonce).phrase,
    enonce,
  };
}

/** « Étape 3 sur 7 » et ce qui la distingue. */
export function direLEtape(r: RejeuDuTrace, rang: number): string {
  const total = r.construction.etapes.length;
  return `Étape ${rang} sur ${total}${rang === r.deviation ? ' · la construction dévie ici' : ''}`;
}

/** Les objets que le tuteur peut toucher pour y ancrer une note. */
export function objetsATracer(
  trace: TraceDeTravailDto,
  figureDEnonce: unknown,
): readonly ObjetDesignable[] {
  return objetsDesignables(trace.eleve, planDEnonce(figureDEnonce));
}

/**
 * L'objet de l'élève sous un clic (un point de la figure) : l'aimant de
 * l'atelier, sans le calque de l'énoncé. `null` : rien de désignable là.
 */
export function objetSousLeClic(
  trace: TraceDeTravailDto,
  figureDEnonce: unknown,
  p: PointV2,
  rayon: number,
): IdObjet | null {
  const enonce = planDEnonce(figureDEnonce);
  const resolue = resoudreConstruction(trace.eleve, enonce);
  const cible = aimanter(p, resolue, rayon, { grille: null, calque: false });
  const id = cible.genre === 'point' || cible.genre === 'objet' ? cible.id : null;
  if (id === null || estIdEnonce(id)) return null;
  return objetsDesignables(trace.eleve, enonce).some((o) => o.id === id) ? id : null;
}
